/**
 * Resolve Modal Component
 * 
 * Modal for resolving disputes
 * Documents the resolution for all parties
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BaseActionModal, { IActionModalProps, IModalChildProps } from './BaseActionModal';
import { ArbitrationAction, DisputeType } from '../../../services/arbitrationService';

interface ResolveModalProps extends Omit<IActionModalProps, 'action'> {
  disputeType: DisputeType;
}

// Resolution types based on dispute type
const RESOLUTION_TYPES: Record<DisputeType, Array<{ value: string; label: string }>> = {
  [DisputeType.ORDER_ITEM]: [
    { value: 'buyer_favor', label: 'به نفع خریدار' },
    { value: 'seller_favor', label: 'به نفع فروشنده' },
    { value: 'mutual_agreement', label: 'توافق دو طرفه' },
    { value: 'partial_refund', label: 'بازپرداخت جزئی' },
    { value: 'replacement', label: 'جایگزینی محصول' },
    { value: 'other', label: 'سایر' }
  ],
  [DisputeType.ORDER]: [
    { value: 'buyer_favor', label: 'به نفع خریدار' },
    { value: 'seller_favor', label: 'به نفع فروشنده' },
    { value: 'mutual_agreement', label: 'توافق دو طرفه' },
    { value: 'other', label: 'سایر' }
  ],
  [DisputeType.INSPECTION]: [
    { value: 'buyer_favor', label: 'به نفع خریدار' },
    { value: 'seller_favor', label: 'به نفع فروشنده' },
    { value: 'mutual_agreement', label: 'توافق دو طرفه' },
    { value: 'other', label: 'سایر' }
  ]
};

const ResolveModalContent: React.FC<IModalChildProps & { disputeType: DisputeType }> = ({
  payload,
  setPayload,
  setIsValid,
  disputeType
}) => {
  const [resolutionType, setResolutionType] = useState('');
  const [resolution, setResolution] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const resolutionOptions = RESOLUTION_TYPES[disputeType] || RESOLUTION_TYPES[DisputeType.ORDER_ITEM];

  // Available tags for categorization
  const availableTags = [
    'تخلف فروشنده',
    'سوءتفاهم',
    'مشکل فنی',
    'تأخیر ارسال',
    'کیفیت محصول',
    'عدم تطابق',
    'کلاهبرداری'
  ];

  useEffect(() => {
    const resolutionText = resolutionType === 'other' 
      ? resolution 
      : `${resolutionOptions.find(r => r.value === resolutionType)?.label || ''}: ${resolution}`;
    
    setPayload({
      resolution: resolutionText,
      resolutionType,
      tags
    });
    setIsValid(resolutionType !== '' && resolution.length >= 10);
  }, [resolutionType, resolution, tags, setPayload, setIsValid, resolutionOptions]);

  const toggleTag = (tag: string) => {
    setTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <Box>
      <Alert severity="success" icon={<HandshakeIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          با حل اختلاف، چت داوری بسته می‌شود و نتیجه به همه طرفین اطلاع‌رسانی خواهد شد.
        </Typography>
      </Alert>

      {/* Resolution Type */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>نوع تصمیم</InputLabel>
        <Select
          value={resolutionType}
          onChange={(e) => setResolutionType(e.target.value)}
          label="نوع تصمیم"
        >
          {resolutionOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Tags */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          برچسب‌های مرتبط (اختیاری)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {availableTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => toggleTag(tag)}
              color={tags.includes(tag) ? 'primary' : 'default'}
              variant={tags.includes(tag) ? 'filled' : 'outlined'}
              size="small"
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
      </Box>

      {/* Resolution Details */}
      <TextField
        fullWidth
        multiline
        rows={4}
        label="شرح تصمیم"
        placeholder="توضیحات کامل در مورد نحوه حل اختلاف را بنویسید..."
        value={resolution}
        onChange={(e) => setResolution(e.target.value)}
        helperText={`${resolution.length}/10 حداقل کاراکتر - این متن برای طرفین ارسال می‌شود`}
        error={resolution.length > 0 && resolution.length < 10}
      />

      {/* Preview */}
      {resolutionType && resolution.length >= 10 && (
        <Box sx={{ 
          mt: 3,
          p: 2, 
          bgcolor: 'success.50', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'success.200'
        }}>
          <Typography variant="subtitle2" color="success.dark" gutterBottom>
            پیش‌نمایش تصمیم:
          </Typography>
          <Typography variant="body2">
            <strong>{resolutionOptions.find(r => r.value === resolutionType)?.label}:</strong> {resolution}
          </Typography>
          {tags.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {tags.map(tag => (
                <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ResolveModal: React.FC<ResolveModalProps> = ({ disputeType, ...props }) => {
  return (
    <BaseActionModal {...props} action={ArbitrationAction.RESOLVE}>
      <ResolveModalContent 
        disputeType={disputeType}
        payload={{}} 
        setPayload={() => {}} 
        isValid={false} 
        setIsValid={() => {}} 
      />
    </BaseActionModal>
  );
};

export default ResolveModal;

